/**
 * Face detection and verification using ReallyAssess Face API
 *
 * Uses InsightFace with ArcFace model for real face recognition.
 * All operations require a valid session token.
 */

import { debug } from '../utils/debug'

// API URL - dedicated Face API for ReallyAssess
const FACE_API_URL = 'https://reallyassess-face-api-dev.techtree.workers.dev'

// Store enrolled face encoding ID
let enrolledEncodingId: string | null = null

// TypeScript types for API responses
interface EnrollResponse {
  success: boolean
  encoding_id: string
  face_count: number
}

interface VerifyResponse {
  verified: boolean
  similarity: number
  faces_detected: number
  error?: string
}

interface ErrorResponse {
  error?: string
  message?: string
}

/**
 * Initialize the face detector (no-op for API-based detection)
 */
export async function initFaceDetector(): Promise<void> {
  return Promise.resolve()
}

/**
 * Enroll a face for later verification using session token
 */
export async function enrollFaceFromBase64(
  base64Image: string,
  sessionToken: string
): Promise<{ success: boolean; encodingId?: string; error?: string }> {
  // Remove data URL prefix if present (optimized - single pass)
  const base64 = base64Image.indexOf(',') !== -1
    ? base64Image.substring(base64Image.indexOf(',') + 1)
    : base64Image

  try {
    const response = await fetch(`${FACE_API_URL}/api/sessions/enroll`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`,
      },
      body: JSON.stringify({
        image_base64: base64,
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' })) as ErrorResponse
      if (errorData.error === 'no_face_detected' || response.status === 400) {
        return { success: false, error: 'No face detected' }
      }
      if (response.status === 401) {
        return { success: false, error: 'Session expired. Please start over.' }
      }
      return { success: false, error: errorData.message || 'Enrollment failed' }
    }

    const result = await response.json() as EnrollResponse
    enrolledEncodingId = result.encoding_id
    return { success: true, encodingId: result.encoding_id }
  } catch (error) {
    return { success: false, error: String(error) }
  }
}
/**
 * Verify a face using session token
 */
export async function verifyFaceFromBase64(
  base64Image: string,
  _encodingId?: string,  // Kept for backwards compatibility but not used
  sessionToken?: string
): Promise<{
  verified: boolean
  facesDetected: number
  similarity?: number
  error?: string
}> {
  if (!sessionToken) {
    // No session token - cannot verify
    return checkFacePresenceFromBase64(base64Image).then(result => ({
      verified: false,
      facesDetected: result.faceCount,
      similarity: 0,
      error: result.hasFace ? 'no_session' : 'no_face_detected',
    }))
  }

  // Remove data URL prefix if present (optimized - single pass)
  const base64 = base64Image.indexOf(',') !== -1
    ? base64Image.substring(base64Image.indexOf(',') + 1)
    : base64Image

  console.log('[FaceDetection] Verifying with session token, image size:', base64.length)

  try {
    // Add timeout to prevent hanging requests
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15000) // 15 second timeout

    const response = await fetch(`${FACE_API_URL}/api/sessions/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`,
      },
      body: JSON.stringify({
        image_base64: base64,
      }),
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    // Handle 401 - session expired
    if (response.status === 401) {
      return {
        verified: false,
        facesDetected: 0,
        similarity: 0,
        error: 'session_expired',
      }
    }

    // Handle 404 - not enrolled
    if (response.status === 404) {
      return checkFacePresenceFromBase64(base64Image).then(result => ({
        verified: false,
        facesDetected: result.faceCount,
        similarity: 0,
        error: 'encoding_expired',
      }))
    }

    const result = await response.json() as VerifyResponse
    console.log('[FaceDetection] API response:', result)

    if (result.error === 'no_face_detected') {
      return {
        verified: false,
        facesDetected: 0,
        similarity: 0,
        error: 'no_face_detected',
      }
    }

    return {
      verified: result.verified,
      facesDetected: result.faces_detected || 1,
      similarity: result.similarity,
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error)
    console.error('[FaceDetection] Verification error:', errorMessage)
    debug.error('[Face] Verification error:', error)
    return { verified: false, facesDetected: 0, error: errorMessage }
  }
}
/**
 * Check face presence - fallback when session is not available
 * Returns false since we can't verify without a session
 */
export async function checkFacePresenceFromBase64(
  _base64Image: string
): Promise<{ hasFace: boolean; faceCount: number }> {
  // Without a session, we can't call the API
  // Return failure - cannot verify identity without session
  return { hasFace: false, faceCount: 0 }
}

/**
 * Check if detector is ready (always true for API-based)
 */
export function isDetectorReady(): boolean {
  return true
}

/**
 * Check if currently initializing (always false for API-based)
 */
export function isDetectorInitializing(): boolean {
  return false
}

/**
 * Clear enrolled face data
 */
export function clearEnrolledFace(): void {
  enrolledEncodingId = null
}

/**
 * Get the current enrolled encoding ID
 */
export function getEnrolledEncodingId(): string | null {
  return enrolledEncodingId
}

/**
 * Set the enrolled encoding ID (for restoring from storage)
 */
export function setEnrolledEncodingId(id: string | null): void {
  enrolledEncodingId = id
}
